import type { ProfileCardProps } from "./ProfileCard";

export type Theme =
  | "01-asian-theme"
  | "02-united-states-theme"
  | "03-caucasian-theme";

/**
 * Landing intent – aligns with onboarding "What brings you here?" options.
 * When set, grid cards show intent-specific phrases instead of theme defaults.
 */
export type LandingIntent =
  | "community"
  | "dating"
  | "friends"
  | "activities"
  | "casual";

/** Exactly 9 phrases per intent (one per card in the grid). */
export const PHRASES_BY_INTENT: Record<LandingIntent, string[]> = {
  community: [
    "Find your community.",
    "Pride month or every month?",
    "Events, meetups, real connection.",
    "Your chosen family is here.",
    "What brings you to the community?",
    "Who's your support system?",
    "Best Pride memory?",
    "Where do you feel most welcome?",
    "Community over everything?",
  ],
  dating: [
    "First date: coffee or drinks?",
    "Looking for something real?",
    "Dates, not just swipes.",
    "Who's your type?",
    "Relationship or seeing where it goes?",
    "Love language: words or touch?",
    "Best first date you've had?",
    "Ready to find your person?",
    "What makes you swipe right?",
  ],
  friends: [
    "Brunch or bottomless mimosas?",
    "What's your pride anthem?",
    "Who's your favorite queen?",
    "Coffee or cocktails?",
    "Gym or couch potato?",
    "Beyonce or Gaga?",
    "What's your karaoke go-to?",
    "What's your comfort show?",
    "Looking for your people?",
  ],
  activities: [
    "Gym or couch potato?",
    "Hiking buddy or travel buddy?",
    "What are you binge-watching?",
    "Connect over what you love.",
    "What's your go-to weekend plan?",
    "Coffee or cocktails?",
    "Best concert you've been to?",
    "What's your karaoke go-to?",
    "Same interests, real meetups?",
  ],
  casual: [
    "Right now or planning ahead?",
    "Casual vibes only.",
    "See who's on now.",
    "What are you up to tonight?",
    "Spontaneous or planned?",
    "No pressure, just vibes.",
    "Tonight or another night?",
    "See what happens?",
    "Your place or mine?",
  ],
};

export const themeConfigs: Record<Theme, ProfileCardProps[]> = {
  "01-asian-theme": [
    {
      color1: "#FEFCE8",
      color2: "#E6F3FF",
      language: "English",
      phrase: "Brunch or bottomless mimosas?",
      imageSrc: require("@/assets/images/screens/landing/01-asian-theme/01.webp"),
    },
    {
      color1: "#E0F2FE",
      color2: "#E0E7FF",
      language: "English",
      phrase: "What's your pride anthem?",
      imageSrc: require("@/assets/images/screens/landing/01-asian-theme/02.webp"),
    },
    {
      color1: "#E0F2FE",
      color2: "#E0E7FF",
      language: "English",
      phrase: "Who's your favorite queen?",
      imageSrc: require("@/assets/images/screens/landing/01-asian-theme/03.webp"),
    },
    {
      color1: "#DCFCE7",
      color2: "#E6F3FF",
      language: "English",
      phrase: "What are you binge-watching?",
      imageSrc: require("@/assets/images/screens/landing/01-asian-theme/04.webp"),
    },
    {
      color1: "#F3E8FF",
      color2: "#E6F3FF",
      language: "English",
      phrase: "Gym or couch potato?",
      imageSrc: require("@/assets/images/screens/landing/01-asian-theme/05.webp"),
    },
    {
      color1: "#FEE2E2",
      color2: "#E6F3FF",
      language: "English",
      phrase: "Beyonce or Gaga?",
      imageSrc: require("@/assets/images/screens/landing/01-asian-theme/06.webp"),
    },
    {
      color1: "#FEF3C7",
      color2: "#E6F3FF",
      language: "English",
      phrase: "What's your karaoke go-to?",
      imageSrc: require("@/assets/images/screens/landing/01-asian-theme/07.webp"),
    },
    {
      color1: "#F3E8FF",
      color2: "#E6F3FF",
      language: "English",
      phrase: "Coffee or cocktails?",
      imageSrc: require("@/assets/images/screens/landing/01-asian-theme/08.webp"),
    },
    {
      color1: "#FEF3C7",
      color2: "#E6F3FF",
      language: "English",
      phrase: "What's your comfort show?",
      imageSrc: require("@/assets/images/screens/landing/01-asian-theme/09.webp"),
    },
  ],
  "02-united-states-theme": [
    {
      color1: "#FEFCE8",
      color2: "#E6F3FF",
      language: "English",
      phrase: "Brunch or bottomless mimosas?",
      imageSrc: require("@/assets/images/screens/landing/02-united-states-theme/01.webp"),
    },
    {
      color1: "#E0F2FE",
      color2: "#E0E7FF",
      language: "English",
      phrase: "What's your pride anthem?",
      imageSrc: require("@/assets/images/screens/landing/02-united-states-theme/03.webp"),
    },
    {
      color1: "#E0F2FE",
      color2: "#E0E7FF",
      language: "English",
      phrase: "Who's your favorite queen?",
      imageSrc: require("@/assets/images/screens/landing/02-united-states-theme/07.webp"),
    },
    {
      color1: "#DCFCE7",
      color2: "#E6F3FF",
      language: "English",
      phrase: "What are you binge-watching?",
      imageSrc: require("@/assets/images/screens/landing/02-united-states-theme/04.webp"),
    },
    {
      color1: "#F3E8FF",
      color2: "#E6F3FF",
      language: "English",
      phrase: "Gym or couch potato?",
      imageSrc: require("@/assets/images/screens/landing/02-united-states-theme/05.webp"),
    },
    {
      color1: "#FEE2E2",
      color2: "#E6F3FF",
      language: "English",
      phrase: "Beyonce or Gaga?",
      imageSrc: require("@/assets/images/screens/landing/02-united-states-theme/06.webp"),
    },
    {
      color1: "#FEF3C7",
      color2: "#E6F3FF",
      language: "English",
      phrase: "What's your karaoke go-to?",
      imageSrc: require("@/assets/images/screens/landing/02-united-states-theme/02.webp"),
    },
    {
      color1: "#F3E8FF",
      color2: "#E6F3FF",
      language: "English",
      phrase: "Coffee or cocktails?",
      imageSrc: require("@/assets/images/screens/landing/02-united-states-theme/08.webp"),
    },
    {
      color1: "#FEF3C7",
      color2: "#E6F3FF",
      language: "English",
      phrase: "What's your comfort show?",
      imageSrc: require("@/assets/images/screens/landing/02-united-states-theme/09.webp"),
    },
  ],
  "03-caucasian-theme": [
    {
      color1: "#FEFCE8",
      color2: "#E6F3FF",
      language: "English",
      phrase: "Brunch or bottomless mimosas?",
      imageSrc: require("@/assets/images/screens/landing/03-caucasian-theme/01.webp"),
    },
    {
      color1: "#E0F2FE",
      color2: "#E0E7FF",
      language: "English",
      phrase: "What's your pride anthem?",
      imageSrc: require("@/assets/images/screens/landing/03-caucasian-theme/02.webp"),
    },
    {
      color1: "#E0F2FE",
      color2: "#E0E7FF",
      language: "English",
      phrase: "Who's your favorite queen?",
      imageSrc: require("@/assets/images/screens/landing/03-caucasian-theme/03.webp"),
    },
    {
      color1: "#DCFCE7",
      color2: "#E6F3FF",
      language: "English",
      phrase: "What are you binge-watching?",
      imageSrc: require("@/assets/images/screens/landing/03-caucasian-theme/04.webp"),
    },
    {
      color1: "#F3E8FF",
      color2: "#E6F3FF",
      language: "English",
      phrase: "Gym or couch potato?",
      imageSrc: require("@/assets/images/screens/landing/03-caucasian-theme/05.webp"),
    },
    {
      color1: "#FEE2E2",
      color2: "#E6F3FF",
      language: "English",
      phrase: "Beyonce or Gaga?",
      imageSrc: require("@/assets/images/screens/landing/03-caucasian-theme/06.webp"),
    },
    {
      color1: "#FEF3C7",
      color2: "#E6F3FF",
      language: "English",
      phrase: "What's your karaoke go-to?",
      imageSrc: require("@/assets/images/screens/landing/03-caucasian-theme/07.webp"),
    },
    {
      color1: "#F3E8FF",
      color2: "#E6F3FF",
      language: "English",
      phrase: "Coffee or cocktails?",
      imageSrc: require("@/assets/images/screens/landing/03-caucasian-theme/08.webp"),
    },
    {
      color1: "#FEF3C7",
      color2: "#E6F3FF",
      language: "English",
      phrase: "What's your comfort show?",
      imageSrc: require("@/assets/images/screens/landing/03-caucasian-theme/09.webp"),
    },
  ],
};
